import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
} from "@chakra-ui/react";
import { LoanTypeViewModel } from "./loan-type.view-model";
import { getErrorMessage } from "utils/errorHandler";
import { useContext, useState } from "react";
import { AlertsContext } from "providers/alerts";
import { apiRequest } from "utils/api";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface LoanTypeModalProps {
  loanType: LoanTypeViewModel;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (loanType: LoanTypeViewModel) => void;
  onUpdate: (loanType: LoanTypeViewModel) => void;
}

export function LoanTypeModal({ loanType, isOpen, onClose, onAdd, onUpdate }: LoanTypeModalProps) {
  const { user } = useTenant();
  const { showSuccess, showError } = useContext(AlertsContext);

  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState(loanType.title);
  const [description, setDescription] = useState<string>(loanType.description ?? "");

  const modalSubmitText = loanType.id ? "Update Loan Type" : "Add Loan Type";

  async function handle(fn: () => Promise<LoanTypeViewModel>, errorTitle: string) {
    try {
      setLoading(true);

      const loanType = await fn();

      showSuccess({
        title: "Success",
        description: "The request was successful!",
      });

      onClose();

      return loanType;
    } catch (err) {
      showError({
        title: errorTitle,
        description: getErrorMessage(err),
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function updateLoanType(): Promise<LoanTypeViewModel> {
    const updateLoanType = {
      title: title,
      description: description,
    };

    await apiRequest("/supabase/loan_types/update", {
      loan_type_id: loanType.id,
      loan_type: {
        title,
        description,
      },
    });

    return {
      ...loanType,
      ...updateLoanType,
    };
  }

  async function addLoanType(): Promise<LoanTypeViewModel> {
    const { loan_type } = await apiRequest<{ loan_type: LoanTypeViewModel }>(
      "/supabase/loan_types/insert",
      {
        title,
        description,
      }
    );

    loan_type.fields = [];

    return loan_type;
  }

  async function handleSaveLoanType() {
    if (!title) {
      showError({
        title: "Error",
        description: "Please, fill in all required fields.",
      });
      return;
    }

    if (loanType.id) {
      const result = await handle(updateLoanType, "Update Failed");
      onUpdate(result);
    } else {
      const result = await handle(addLoanType, "Add Failed");
      onAdd(result);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Loan Type</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb="12px" isRequired>
            <FormLabel>Title</FormLabel>
            <Input
              value={title}
              onChange={ev => setTitle(ev.target.value)}
              variant="primary"
              placeholder="Title"
            />
          </FormControl>
          <FormControl mb="12px">
            <FormLabel>Description</FormLabel>

            <Textarea
              value={description}
              onChange={ev => setDescription(ev.target.value)}
              variant="primary"
              placeholder="Description"
            />
          </FormControl>
        </ModalBody>

        <ModalFooter alignItems="end" gap={2}>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={getCustomButtonVariant(user)}
            onClick={handleSaveLoanType}
            isLoading={loading}
            leftIcon={<FontAwesomeIcon icon={["far", "save"]} />}
          >
            {modalSubmitText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
